import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import {
  Zap, LayoutTemplate, Upload, Bot, BarChart2, Menu, X, LogIn,
  Globe, LogOut, ChevronDown, UserCircle
} from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { useTranslation } from '../i18n/useTranslation';
import { authService, isFirebaseConfigured } from '../services/firebase';
import { clsx } from 'clsx';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, user, setUser, setMySites, resetBuilder, showToast } = useAppStore();
  const { t, lang, setLang } = useTranslation();

  const navLinks = [
    { path: '/', label: t('nav.home'), icon: Zap },
    { path: '/gallery', label: t('nav.gallery'), icon: LayoutTemplate },
    { path: '/import', label: t('nav.import'), icon: Upload },
    { path: '/connect', label: t('nav.publish'), icon: Globe },
    { path: '/dashboard', label: t('nav.dashboard'), icon: BarChart2 },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Tutup menu bila klik luar
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      if (isFirebaseConfigured) {
        await authService.signOut();
      }
      setUser(null);
      setMySites([]);
      resetBuilder();
      setUserMenuOpen(false);
      setMobileOpen(false);
      showToast('Berjaya log keluar 👋', 'success');
      navigate('/');
    } catch {
      showToast('Gagal log keluar', 'error');
    }
  };

  return (
    <header className="bg-white border-b border-surface-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 telegram-gradient rounded-lg flex items-center justify-center shadow-glow group-hover:scale-105 transition-transform">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-white fill-current">
                <path d="M12 2L2 12l3.5 3.5L12 9l6.5 6.5L22 12z"/>
                <path d="M2 12l10 10 10-10-6.5-6.5z" opacity="0.6"/>
              </svg>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-extrabold text-lg text-surface-900">Tele</span>
              <span className="font-extrabold text-lg text-telegram">Web</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150',
                  isActive(path)
                    ? 'bg-telegram/10 text-telegram'
                    : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900'
                )}
              >
                <Icon size={15} />
                {label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-2">
            {/* Language Toggle */}
            <button
              onClick={() => setLang(lang === 'ms' ? 'en' : 'ms')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-surface-200 hover:border-telegram/50 hover:bg-telegram/5 transition-all text-sm font-medium text-surface-600"
              title="Switch language / Tukar bahasa"
            >
              <Globe size={14} className="text-telegram" />
              <span className={lang === 'ms' ? 'text-telegram font-bold' : ''}>BM</span>
              <span className="text-surface-300">|</span>
              <span className={lang === 'en' ? 'text-telegram font-bold' : ''}>EN</span>
            </button>

            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                {/* Avatar Button */}
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 pl-2 pr-2 py-1.5 rounded-xl hover:bg-surface-100 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-telegram text-white flex items-center justify-center text-sm font-bold">
                    {user?.displayName?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm font-medium text-surface-700 max-w-[100px] truncate hidden lg:inline">
                    {user?.displayName}
                  </span>
                  <ChevronDown size={14} className={clsx('text-surface-400 transition-transform', userMenuOpen && 'rotate-180')} />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <div className="absolute right-0 top-12 bg-white rounded-xl shadow-card-hover border border-surface-200 py-1.5 z-50 min-w-[220px]">
                    {/* User info header */}
                    <div className="px-4 py-3 border-b border-surface-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-full bg-telegram text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {user?.displayName?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-surface-900 truncate">{user?.displayName}</div>
                          <div className="text-xs text-surface-500 truncate">{user?.email}</div>
                        </div>
                      </div>
                      {user?.role === 'admin' && (
                        <div className="mt-2">
                          <span className="badge-purple text-xs">👑 Admin</span>
                        </div>
                      )}
                    </div>

                    {/* Menu items */}
                    <Link
                      to="/dashboard"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-surface-700 hover:bg-surface-50 transition-colors"
                    >
                      <BarChart2 size={15} className="text-telegram" />
                      {t('nav.dashboard')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut size={15} />
                      Log Keluar
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn-outline btn-sm">
                  <LogIn size={15} />
                  {t('nav.login')}
                </Link>
                <Link to="/gallery" className="btn-primary btn-sm">
                  {t('nav.start_free')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Language Toggle */}
            <button
              onClick={() => setLang(lang === 'ms' ? 'en' : 'ms')}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-surface-200 text-xs font-bold text-surface-600"
            >
              <Globe size={12} className="text-telegram" />
              {lang === 'ms' ? 'BM' : 'EN'}
            </button>
            <button
              className="p-2 rounded-lg hover:bg-surface-100"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-surface-200 bg-white px-4 py-3">
          {isAuthenticated && (
            <div className="flex items-center gap-3 p-3 bg-surface-50 rounded-xl mb-3">
              <div className="w-10 h-10 rounded-full bg-telegram text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {user?.displayName?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-surface-900">{user?.displayName}</div>
                <div className="text-xs text-surface-500 truncate">{user?.email}</div>
              </div>
            </div>
          )}
          {navLinks.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setMobileOpen(false)}
              className={clsx(
                'flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-colors',
                isActive(path)
                  ? 'bg-telegram/10 text-telegram'
                  : 'text-surface-600 hover:bg-surface-100'
              )}
            >
              <Icon size={16} />
              {label}
            </Link>
          ))}
          <div className="pt-2 mt-2 border-t border-surface-200 flex gap-2">
            {isAuthenticated ? (
              <button onClick={handleLogout} className="btn btn-danger btn-md flex-1 justify-center">
                <LogOut size={16} />
                Log Keluar
              </button>
            ) : (
              <>
                <Link to="/login" className="btn-outline btn-sm flex-1 justify-center">
                  {t('nav.login')}
                </Link>
                <Link to="/gallery" className="btn-primary btn-sm flex-1 justify-center">
                  {t('nav.start_free')}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
